import type { ButtonHTMLAttributes, ReactNode } from 'react';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'ink' | 'ghost' | 'text';
  size?: 'sm' | 'lg';
  block?: boolean;
}

export function Button({
  variant = 'primary',
  size,
  block,
  className = '',
  children,
  type = 'button',
  ...rest
}: ButtonProps) {
  const classes = [
    'btn',
    `btn-${variant}`,
    size ? `btn-${size}` : '',
    block ? 'btn-block' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <button type={type} className={classes} {...rest}>
      {children}
    </button>
  );
}

type ChipVariant = 'lime' | 'soft' | 'ink' | 'coral' | 'blue' | 'outline';

interface ChipProps {
  variant?: ChipVariant;
  dot?: boolean;
  children: ReactNode;
}

export function Chip({ variant = 'soft', dot, children }: ChipProps) {
  return (
    <span className={`chip chip-${variant}`}>
      {dot && <span className="chip-dot" />}
      {children}
    </span>
  );
}

interface AvatarProps {
  initials: string;
  color?: string;
  size?: number;
  dark?: boolean;
}

export function Avatar({ initials, color = '#C8FF4A', size = 32, dark = false }: AvatarProps) {
  return (
    <div
      className="avatar"
      style={{
        width: size,
        height: size,
        background: color,
        color: dark ? 'var(--cream)' : 'var(--ink)',
        fontSize: Math.round(size * 0.4),
      }}
    >
      {initials}
    </div>
  );
}

interface AvatarStackProps {
  people: Array<{ initials: string; color: string; dark?: boolean }>;
  size?: number;
  max?: number;
}

export function AvatarStack({ people, size = 28, max = 4 }: AvatarStackProps) {
  const visible = people.slice(0, max);
  const more = people.length - max;
  return (
    <div style={{ display: 'flex' }}>
      {visible.map((p, i) => (
        <div
          key={`${p.initials}-${i}`}
          style={{
            marginLeft: i === 0 ? 0 : -8,
            boxShadow: '0 0 0 2px var(--cream-50)',
            borderRadius: 999,
          }}
        >
          <Avatar initials={p.initials} color={p.color} size={size} dark={p.dark} />
        </div>
      ))}
      {more > 0 && (
        <div
          style={{
            marginLeft: -8,
            boxShadow: '0 0 0 2px var(--cream-50)',
            borderRadius: 999,
          }}
        >
          <Avatar initials={`+${more}`} color="#EFEBE2" size={size} />
        </div>
      )}
    </div>
  );
}

interface FieldProps {
  label?: string;
  hint?: string;
  error?: string;
  children: ReactNode;
  htmlFor?: string;
}

export function Field({ label, hint, error, children, htmlFor }: FieldProps) {
  return (
    <div>
      {label && (
        <label className="field-label" htmlFor={htmlFor}>
          {label}
        </label>
      )}
      {children}
      {hint && !error && (
        <div style={{ fontSize: 12, color: 'var(--fg-muted)', marginTop: 6 }}>{hint}</div>
      )}
      {error && (
        <div style={{ fontSize: 12, color: 'var(--coral-deep)', marginTop: 6 }}>{error}</div>
      )}
    </div>
  );
}
